"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import SplitText from "@/components/bits/SplitText/SplitText";
import EpisodeCards from "@/components/episode-cards";
import AuroraBackground from "@/components/aurora-background";
import { Session } from "@/lib/user";
import { Vibrant } from "node-vibrant/browser";
import { notoSerif } from "@/lib/fonts";
import { getAuroraPaletteHexes } from "@/lib/aurora-palette";

const getFirstName = (name?: string | null) => {
  const trimmedName = name?.trim();

  if (!trimmedName) {
    return "there";
  }

  return trimmedName.split(/\s+/)[0] || "there";
};

const buildHomeGreeting = (name?: string | null) => {
  const safeName = getFirstName(name);
  const greetings = [
    `There you are, ${safeName}.`,
    `Look who's back, ${safeName}.`,
    `Lots to catch up on, ${safeName}.`,
    `Oh hey, ${safeName}.`,
    `Saved you a seat, ${safeName}.`,
    `Where were we, ${safeName}?`,
    `Still curious, ${safeName}?`,
  ];

  return greetings[Math.floor(Math.random() * greetings.length)];
};

export default function MainPageContent({ session }: { session: Session | null }) {
  const { resolvedTheme } = useTheme();
  const [auroraColors, setAuroraColors] = useState<string[] | null>(null);
  const [showAurora, setShowAurora] = useState(false);
  const [firstEpisodeCover, setFirstEpisodeCover] = useState<string | null>(null);
  const [greeting, setGreeting] = useState(() =>
    session?.user?.name ? `Welcome back, ${getFirstName(session.user.name)}.` : ""
  );

  useEffect(() => {
    if (!session?.user?.name) {
      setGreeting("");
      return;
    }

    setGreeting(buildHomeGreeting(session.user.name));
  }, [session?.user?.name]);

  useEffect(() => {
    let cancelled = false;

    if (!firstEpisodeCover) {
      setShowAurora(false);
      setAuroraColors(null);
      return;
    }

    setShowAurora(false);

    const extractColorsFromCover = async (cover: string) => {
      const wsrvLowResUrl = `https://wsrv.nl/?url=${encodeURIComponent(
        cover
      )}&w=96&h=96&fit=cover&we&output=webp&q=50&maxage=1y&default=${encodeURIComponent(
        "https://wsrv.nl/lichtenstein.jpg"
      )}`;

      const wsrvFallbackUrl = `https://wsrv.nl/?url=${encodeURIComponent(
        cover
      )}&w=64&h=64&fit=cover&we&output=webp&q=45&default=${encodeURIComponent(
        "https://wsrv.nl/lichtenstein.jpg"
      )}`;

      let palette: any;
      try {
        palette = await Vibrant.from(wsrvLowResUrl).getPalette();
      } catch {
        try {
          palette = await Vibrant.from(wsrvFallbackUrl).getPalette();
        } catch {
          palette = await Vibrant.from(cover).getPalette();
        }
      }

      return getAuroraPaletteHexes(
        palette,
        resolvedTheme === "light" ? "light" : "dark"
      );
    };

    const hydrateAurora = async () => {
      try {
        const colors = await extractColorsFromCover(firstEpisodeCover);
        if (!colors || cancelled) return;
        setAuroraColors(colors);
        requestAnimationFrame(() => {
          if (!cancelled) setShowAurora(true);
        });
      } catch {
        // Intentionally keep aurora hidden if extraction fails.
      }
    };

    hydrateAurora();

    return () => {
      cancelled = true;
    };
  }, [firstEpisodeCover, resolvedTheme]);

  return (
    <main className="m-4 relative pb-24">
      {auroraColors ? (
        <div
          className={`transition-opacity duration-700 ease-out ${showAurora ? "opacity-100" : "opacity-0"}`}
        >
          <AuroraBackground
            colorStops={auroraColors}
            opacity={32}
           
          />
        </div>
      ) : null}

      <div className="md:mx-4 mx-2 relative z-10">
        {session ? (
          <div className="flex justify-center md:justify-start">
            <span
              className={`block text-center text-3xl md:text-left md:text-3xl font-semibold tracking-tight ${notoSerif.className}`}
            >
              <SplitText text={greeting} delay={20} />
            </span>
          </div>
        ) : null}
        <EpisodeCards
          session={session}
          onFirstEpisodeCoverChange={setFirstEpisodeCover}
        />
      </div>
    </main>
  );
}
